import { cn } from "~/lib/utils";
import { useI18n } from "~/i18n";
import { formatActivityTime } from "./activityPresentation";
import type { ActivityLifecycleStep, ActivityStepState } from "./activityPresentation";

function stepDotClass(state: ActivityStepState): string {
  switch (state) {
    case "done":
      return "border-emerald-500 bg-emerald-500";
    case "current":
      return "border-primary bg-primary ring-2 ring-primary/30";
    case "error":
      return "border-destructive bg-destructive";
    default:
      return "border-border bg-muted";
  }
}

function connectorClass(leftState: ActivityStepState): string {
  if (leftState === "error") return "bg-destructive/40";
  if (leftState === "done") return "bg-emerald-500/50";
  if (leftState === "current") return "bg-primary/35";
  return "bg-border";
}

export function ActivityLifecycleStepper({
  steps,
  compact = false,
}: {
  readonly steps: readonly ActivityLifecycleStep[];
  readonly compact?: boolean;
}) {
  const { t, locale } = useI18n();
  if (steps.length === 0) return null;

  if (compact) {
    return (
      <div
        className="flex items-center gap-0"
        role="list"
        aria-label={t("activities.detail.progress")}
      >
        {steps.map((step, index) => (
          <div key={`${step.labelKey}-${index}`} className="flex min-w-0 flex-1 items-center">
            <span
              role="listitem"
              className={cn("size-2 shrink-0 rounded-full border", stepDotClass(step.state))}
              title={t(step.labelKey)}
            />
            {index < steps.length - 1 ? (
              <span
                className={cn(
                  "mx-0.5 h-0.5 min-w-[0.35rem] flex-1 rounded-full",
                  connectorClass(step.state),
                )}
                aria-hidden
              />
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-0" role="list">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <li key={`${step.labelKey}-${index}`} className="flex gap-3">
            <div className="flex w-4 shrink-0 flex-col items-center">
              <span
                className={cn("size-2.5 shrink-0 rounded-full border-2", stepDotClass(step.state))}
                aria-hidden
              />
              {!isLast ? (
                <span
                  className={cn(
                    "my-0.5 w-0.5 min-h-[1.25rem] flex-1 rounded-full",
                    connectorClass(step.state),
                  )}
                  aria-hidden
                />
              ) : null}
            </div>
            <div className="min-w-0 flex-1 pb-3">
              <p
                className={cn(
                  "text-sm leading-snug",
                  step.state === "current" && "font-medium text-foreground",
                  step.state === "done" && "text-muted-foreground",
                  step.state === "upcoming" && "text-muted-foreground/60",
                  step.state === "error" && "font-medium text-destructive",
                )}
              >
                {t(step.labelKey)}
                {step.at ? (
                  <span className="ms-1.5 text-[11px] font-normal tabular-nums text-muted-foreground/70">
                    {formatActivityTime(step.at, locale)}
                  </span>
                ) : null}
              </p>
              {step.detail ? (
                <p
                  className={cn(
                    "mt-1 text-xs leading-relaxed",
                    step.state === "upcoming"
                      ? "text-muted-foreground/55"
                      : "text-muted-foreground",
                  )}
                >
                  {step.detail}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
